// Хуки данных. Экраны видят только их: ни fetch, ни заголовков, ни токенов.
import { useEffect } from 'react';
import { infiniteQueryOptions, keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, withParams } from './client';
import { clampPollMs } from './pollInterval';
import { hasStaleLive, mergeLive, mergeRows, rowsHaveStaleLive, timezoneOffset } from './live';
import { readStart, startKey, writeStart } from './startCache';
import type { HomeScreen } from './homeTypes';
import type { SearchResponse } from './searchTypes';
import type { FavoritesScreen } from './favoritesTypes';
import type { AppConfig, LiveBatch, LiveRow, MatchesScreen, Poll } from './types';

const CONFIG_PATH = '/api/v5/mobile/config/';
const MATCHES_FALLBACK_PATH = '/api/v5/mobile/screens/cricket/matches/';

// Конфиг прошлого запуска — начальные данные: первый экран запрашиваем сразу, не дожидаясь config (на медленной сети
// это preflight + ответ, ~0,9 с). Он всегда устаревший (updatedAt 0) — свежий конфиг приходит следом и заменяет его.
// Хранение, ключ (сборка, язык) и срок в сутки — startCache (HQ-842).
const CONFIG_KEY = startKey('config');

export function useConfig() {
  return useQuery({
    queryKey: ['config'],
    queryFn: async ({ signal }) => {
      const config = await apiGet<AppConfig>(CONFIG_PATH, signal);
      writeStart(CONFIG_KEY, config);
      return config;
    },
    initialData: () => readStart<AppConfig>(CONFIG_KEY)?.data,
    initialDataUpdatedAt: 0,
    staleTime: 10 * 60_000,
  });
}

// Адреса, ответ которых в этом запуске уже пришёл из сети: данные под ними — не сохранённые прошлым запуском.
const fetched = new Set<string>();

/** Данные экрана — из кэша старта (свежий ответ ещё не пришёл): живой счёт в них помечаем «обновляем…». */
function isRestored(query: { data: unknown; isPlaceholderData: boolean }, id: string): boolean {
  return query.data !== undefined && !query.isPlaceholderData && !fetched.has(id);
}

// «Матчи» прошлого запуска — только день якоря (полотно целиком ~90 КБ, остальные дни приедут свежим ответом) и только
// в тот же календарный день: вчерашний «сегодня» сегодня уже чужой день.
const MATCHES_KEY = startKey('matches');

/** Полотно, урезанное до дня якоря; день якоря без матчей — не сохраняем (пустое полотно ≠ пустой день). */
export function anchorDayOnly(screen: MatchesScreen): MatchesScreen | undefined {
  const date = screen.anchor?.date ?? screen.days.find((d) => d.is_today)?.date;
  const days = screen.days.filter((d) => d.date === date);
  if (!days.length) return undefined;
  return { ...screen, days, ...(screen.calendar ? { calendar: screen.calendar.filter((c) => c.date === date) } : {}) };
}

const sameLocalDay = (a: number, b: number) => new Date(a).toDateString() === new Date(b).toDateString();

function storedMatches(now = Date.now()): MatchesScreen | undefined {
  const saved = readStart<MatchesScreen>(MATCHES_KEY, now);
  return saved && sameLocalDay(saved.savedAt, now) ? saved.data : undefined;
}

/**
 * Полотно матчей + живой опрос. `path` — url вкладки из config как есть; пояс считаем на момент запроса.
 * Путь и частота опроса — только из `poll` ответа; interval_seconds == null → не опрашиваем.
 * Скрытая вкладка/свёрнутое приложение — пауза (refetchIntervalInBackground: false).
 */
export function useMatchesScreen(path: string | undefined) {
  const queryClient = useQueryClient();
  const url = withParams(path ?? MATCHES_FALLBACK_PATH, { timezone_offset: timezoneOffset() });
  const queryKey = ['matches', url];
  const id = `matches ${url}`;
  const screen = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const data = await apiGet<MatchesScreen>(url, signal);
      fetched.add(id);
      const day = anchorDayOnly(data);
      if (day) writeStart(MATCHES_KEY, day);
      return data;
    },
    initialData: () => storedMatches(),
    initialDataUpdatedAt: 0,
    staleTime: 60_000,
  });

  useLiveBatch(screen.data?.poll, (batch) => {
    const current = queryClient.getQueryData<MatchesScreen>(queryKey);
    if (!current) return;
    if (hasStaleLive(current, batch)) void queryClient.invalidateQueries({ queryKey });
    queryClient.setQueryData<MatchesScreen>(queryKey, mergeLive(current, batch));
  });
  return Object.assign(screen, { restored: isRestored(screen, id) });
}

/**
 * Живой батч для любого экрана со строками матчей: путь и частота — только из poll ответа
 * (interval_seconds в пределах 3…600 с; null, 0, мусор → не опрашиваем; скрытое приложение — пауза). onBatch получает тонкие строки.
 */
export function useLiveBatch(poll: Poll | undefined, onBatch: (rows: LiveRow[]) => void) {
  const path = poll?.live_batch_path;
  const interval = clampPollMs(poll?.interval_seconds);
  const live = useQuery({
    queryKey: ['live-batch', path],
    queryFn: ({ signal }) => apiGet<LiveBatch>(path!, signal),
    enabled: Boolean(path) && interval !== false,
    refetchInterval: interval,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
  useEffect(() => {
    if (live.data) onBatch(live.data.matches);
    // onBatch пересоздаётся каждый рендер; зависимость — сам ответ батча
  }, [live.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Главная: личный ответ, не кэшируется сервером. Избранное — из аккаунта (Bearer, этап входа) или параметрами.
 * Живые строки (my_matches / live_now / next_up / who_wins) — мерж батча.
 * Кэш старта — по адресу вкладки (path), а не по набору избранного: набор приезжает с сервера уже после старта, а
 * прошлая главная нужна сразу. Сохраняем только ответ под окончательным набором (save), чтобы ответ «пока без
 * избранного» не затирал личную главную. enabled: false — запрос не уходит (вошедший, набор аккаунта ещё не пришёл:
 * пустые параметры сервер прочёл бы как «избранного нет»), кэш старта при этом показывается как есть.
 */
export function useHome(
  path: string, favorites: Record<string, string>, { save = true, enabled = true }: { save?: boolean; enabled?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const url = withParams(path, favorites);
  const queryKey = ['home', url];
  const id = `home ${url}`;
  const storeKey = startKey('home', path);
  const screen = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const data = await apiGet<HomeScreen>(url, signal);
      fetched.add(id);
      return data;
    },
    initialData: () => readStart<HomeScreen>(storeKey)?.data,
    initialDataUpdatedAt: 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled,
  });
  useLiveBatch(screen.data?.poll, (batch) => {
    const current = queryClient.getQueryData<HomeScreen>(queryKey);
    if (!current) return;
    const lists = (['my_matches', 'live_now', 'next_up'] as const).filter((k) => current[k]?.items);
    const allRows = lists.flatMap((k) => current[k]!.items);
    if (rowsHaveStaleLive(allRows, batch)) void queryClient.invalidateQueries({ queryKey });
    const next: HomeScreen = { ...current };
    for (const k of lists) next[k] = { ...current[k]!, items: mergeRows(current[k]!.items, batch) };
    queryClient.setQueryData<HomeScreen>(queryKey, next);
  });
  const restored = isRestored(screen, id);
  // сохраняем свежий ответ (и его мерж батча), когда набор избранного окончательный — в т. ч. если адрес с ним не изменился
  useEffect(() => {
    if (save && screen.data && !screen.isPlaceholderData && fetched.has(id)) writeStart(storeKey, screen.data);
  }, [save, screen.data]); // eslint-disable-line react-hooks/exhaustive-deps
  return Object.assign(screen, { restored });
}

/** Поиск: запрос уходит после паузы ввода (debounce в экране); прошлый ответ держим, пока едет новый. */
export const SEARCH_PATH = '/api/v5/mobile/screens/cricket/search/';
/** path — action.endpoint кнопки поиска серверной шапки; без шапки — известный адрес. */
/** enabled: false — не спрашивать (поиск «Избранного» с пустым полем рисует свою заглушку, каталог ему не нужен). */
export function useSearch(q: string, path = SEARCH_PATH, enabled = true) {
  const url = withParams(path, { q: q || undefined });
  return useQuery({
    queryKey: ['search', path, q],
    enabled,
    queryFn: ({ signal }) => apiGet<SearchResponse>(url, signal),
    staleTime: 2 * 60_000,
    placeholderData: keepPreviousData,
  });
}

/** Полка избранного: набор — НАШ (параметрами, все 4 ключа), прошлые матчи — страницами по курсору. */
/** tab не задан — стартовую вкладку выбирает сервер (ответ несёт tab).
 *  Ключ — url с набором: новая звезда — новый запрос сразу. staleTime 60 с: возврат в Telegram (фокус) не
 *  перезапрашивает все загруженные страницы прошлых матчей подряд (HQ-1646). */
export function favoritesShelfOptions(path: string, tab: string | undefined, params: Record<string, string>) {
  const url = withParams(path, { tab, ...params, timezone_offset: timezoneOffset() });
  return infiniteQueryOptions({
    queryKey: ['favorites-shelf', url],
    queryFn: ({ pageParam, signal }) => apiGet<FavoritesScreen>(withParams(url, { cursor: pageParam ?? undefined }), signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: FavoritesScreen) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export const useFavoritesShelf = (path: string, tab: string | undefined, params: Record<string, string>) =>
  useInfiniteQuery(favoritesShelfOptions(path, tab, params));
